import type { AxiosRequestConfig } from "axios";
import { api, generateQueryParamFromObj, prefix } from "@/api";
import type { OrganizationEntityType } from "@/types/lead";
import type {
  AreaType,
  OrganizationDetailType,
  OrganizationsMapParamsType,
  organizationType,
  SingleOrganizationMap,
} from "@/types/organization";
import type { GenericParamsType } from "@/types/commons";
import { cancelPreviousRequests, registerNewRequest } from "@/utilities/services/apiQueue";
import { id } from "date-fns/locale";
import LZString from "lz-string";
import { usePolygon } from "@/store/polygons";
import { useSettings } from "@/store/settings";

const namespace = "gaia/organizations";
const namespaceMap = "gaia/maps/organizations";
const mapService = "map";
// Cache per prevenire chiamate duplicate con gli stessi parametri
const MAX_CACHE_SIZE = 20; // Numero massimo di elementi nella cache
const CACHE_TTL = 15 * 60 * 1000; // Tempo di vita della cache: 15 minuti (era 60 secondi)

interface CacheEntry<T> {
  value: Promise<T>;
  timestamp: number;
}

const requestCache = new Map<string, CacheEntry<any>>();

// Artisan Industries API functions
export const getArtisanIndustries = (config: AxiosRequestConfig = {}) => {
  return api<any[]>({
    url: `${prefix}/gaia/artisanindustries`,
    method: "get",
    ...config,
  });
};

export const getOrganizations = async (
  params: {
    status?: string;
    limit?: number | string;
    page?: number | string;
    searchText?: string;
  } = {},
  config: AxiosRequestConfig = {}
) => {
  const settingStore = useSettings();
  const query = generateQueryParamFromObj(params);
  const urlPath = `${prefix}/${settingStore.effectiveVariantConfig.api.organizationsListPath}?${query}`;
  const response = await api<{ data: organizationType[]; count: number }>({
    url: urlPath,
    method: "get",
    ...config,
  });
  return response;
};

function cleanupCache() {
  const now = Date.now();
  for (const [key, entry] of requestCache.entries()) {
    if (now - entry.timestamp > CACHE_TTL || !entry.value) {
      requestCache.delete(key);
    }
  }

  // Limita il numero di elementi
  if (requestCache.size > MAX_CACHE_SIZE) {
    const keys = Array.from(requestCache.keys());
    for (let i = 0; i < keys.length - MAX_CACHE_SIZE; i++) {
      requestCache.delete(keys[i]);
    }
  }

  if (requestCache.size > MAX_CACHE_SIZE * 0.8) {
    console.warn(
      `La cache sta superando l'80% del limite (${requestCache.size}/${MAX_CACHE_SIZE})`
    );
  }
}
// Pulisce ogni 30 secondi
setInterval(cleanupCache, 20000);

// ============================================
// CACHE PERSISTENTE PER LOCATION DATA
// ============================================
const LOCATION_CACHE_VERSION = "v1.0"; // Incrementa per invalidare cache vecchie
const LOCATION_CACHE_KEYS = {
  regions: `location_cache_${LOCATION_CACHE_VERSION}_regions`,
  provinces: `location_cache_${LOCATION_CACHE_VERSION}_provinces_`,
  cities: `location_cache_${LOCATION_CACHE_VERSION}_cities_`,
  zipCodes: `location_cache_${LOCATION_CACHE_VERSION}_zipcodes_`,
  parcels: `location_cache_${LOCATION_CACHE_VERSION}_parcels`, // Particelle utente
  parcelsList: `location_cache_${LOCATION_CACHE_VERSION}_parcels_list`, // Lista particelle dropdown
  allOrganizations: `location_cache_${LOCATION_CACHE_VERSION}_all_organizations`, // Tutte le org in Italia
};

// Map per evitare chiamate duplicate mentre una è già in corso
const pendingCacheRequests = new Map<string, Promise<any>>();

/**
 * Wrapper generico per cache localStorage con fallback
 */
const withLocationCache = async <T>(
  cacheKey: string,
  fetchFn: () => Promise<T>,
  options?: { ttl?: number }
): Promise<T> => {
  try {
    const cachedCompressed = localStorage.getItem(cacheKey);
    // Log spazio totale usato in localStorage
    let totalSize = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length + key.length;
      }
    }

    if (cachedCompressed) {
      try {
        const decompressed = LZString.decompressFromUTF16(cachedCompressed);
        if (!decompressed) throw new Error("Decompression failed");

        const { data, timestamp } = JSON.parse(decompressed);
        const ttl = options?.ttl || Infinity; // Default: cache infinita
        const age = Date.now() - timestamp;

        if (Date.now() - timestamp < ttl) {
          return data;
        } else {
          localStorage.removeItem(cacheKey);
        }
      } catch (error) {
        localStorage.removeItem(cacheKey);
      }
    }

    if (pendingCacheRequests.has(cacheKey)) {
      return await pendingCacheRequests.get(cacheKey)!;
    }

    const fetchPromise = fetchFn()
      .then((data) => {
        // Salva in localStorage
        try {
          const jsonString = JSON.stringify({
            data,
            timestamp: Date.now(),
          });
          const originalSizeKB = Math.round(jsonString.length / 1024);
          const compressed = LZString.compressToUTF16(jsonString);
          const compressedSizeKB = Math.round(compressed.length / 1024);
          const compressionRatio = Math.round((1 - compressedSizeKB / originalSizeKB) * 100);
          localStorage.setItem(cacheKey, compressed);
        } catch (e) {
          // Quota exceeded - pulisci cache vecchie
          console.warn("Storage quota exceeded, clearing old caches");
          clearOldLocationCaches();
          // Riprova
          try {
            const jsonString = JSON.stringify({ data, timestamp: Date.now() });
            const compressed = LZString.compressToUTF16(jsonString);
            localStorage.setItem(cacheKey, compressed);
          } catch (e2) {
            console.error("Unable to cache, continuing without cache");
          }
        }
        pendingCacheRequests.delete(cacheKey);
        return data;
      })
      .catch((error) => {
        pendingCacheRequests.delete(cacheKey);
        throw error;
      });
    pendingCacheRequests.set(cacheKey, fetchPromise);

    return await fetchPromise;
  } catch (error) {
    console.error(`  Error in ${cacheKey}:`, error);
    throw error;
  }
};

const clearOldLocationCaches = () => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("location_cache_") && !key.includes(LOCATION_CACHE_VERSION)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
};

export const invalidateLocationCache = () => {
  Object.values(LOCATION_CACHE_KEYS).forEach((baseKey) => {
    // Rimuovi tutte le varianti (con suffissi dinamici)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(baseKey)) {
        localStorage.removeItem(key);
      }
    }
  });
};

export const invalidateParcelCache = () => {
  const parcelEndpoints = [
    `${prefix}/${mapService}/parcels`,
    `${prefix}/${mapService}/parcels/polygon`,
  ];

  for (const key of requestCache.keys()) {
    for (const endpoint of parcelEndpoints) {
      if (key.includes(endpoint)) {
        requestCache.delete(key);
      }
    }
  }
  localStorage.removeItem(LOCATION_CACHE_KEYS.parcels);
  localStorage.removeItem(LOCATION_CACHE_KEYS.parcelsList);
};

export const updateParcelCacheAfterCreate = (newParcel: any) => {
  try {
    const cachedParcels = localStorage.getItem(LOCATION_CACHE_KEYS.parcels);
    if (cachedParcels) {
      const { data, timestamp } = JSON.parse(cachedParcels);
      data.polygons = data.polygons || [];
      data.polygons.push(newParcel);
      localStorage.setItem(LOCATION_CACHE_KEYS.parcels, JSON.stringify({ data, timestamp }));
      const parcelId = newParcel.properties?.id || newParcel.id;
    }

    const cachedList = localStorage.getItem(LOCATION_CACHE_KEYS.parcelsList);
    if (cachedList) {
      const { data, timestamp } = JSON.parse(cachedList);
      data.parcels = data.parcels || [];
      data.parcels.push(newParcel);
      localStorage.setItem(LOCATION_CACHE_KEYS.parcelsList, JSON.stringify({ data, timestamp }));
    }
  } catch (error) {
    console.error("[Cache] Error updating cache after create:", error);
    invalidateParcelCache();
  }
};

/**
 * Aggiorna la cache localStorage dopo UPDATE di una particella
 * Evita di rifare la GET - modifica la particella esistente nella cache
 */
export const updateParcelCacheAfterUpdate = (parcelId: number, updatedData: any) => {
  try {
    const cachedParcels = localStorage.getItem(LOCATION_CACHE_KEYS.parcels);
    if (cachedParcels) {
      const { data, timestamp } = JSON.parse(cachedParcels);
      const index = data.polygons?.findIndex(
        (p: any) => p.properties?.id === parcelId || p.id === parcelId
      );
      if (index >= 0) {
        if (data.polygons[index].properties) {
          data.polygons[index].properties = { ...data.polygons[index].properties, ...updatedData };
        } else {
          data.polygons[index] = { ...data.polygons[index], ...updatedData };
        }
        localStorage.setItem(LOCATION_CACHE_KEYS.parcels, JSON.stringify({ data, timestamp }));
      } else {
        console.warn(`[Cache] Parcel id ${parcelId} not found in parcels cache`);
      }
    }

    const cachedList = localStorage.getItem(LOCATION_CACHE_KEYS.parcelsList);
    if (cachedList) {
      const { data, timestamp } = JSON.parse(cachedList);
      const index = data.parcels?.findIndex((p: any) => p.id === parcelId);
      if (index >= 0) {
        data.parcels[index] = { ...data.parcels[index], ...updatedData };
        localStorage.setItem(LOCATION_CACHE_KEYS.parcelsList, JSON.stringify({ data, timestamp }));
      }
    }
  } catch (error) {
    console.error("[Cache] Error updating cache after update:", error);
    // In caso di errore, invalida cache per sicurezza
    invalidateParcelCache();
  }
};

export const updateParcelCacheAfterDelete = (parcelId: number) => {
  try {
    const cachedParcels = localStorage.getItem(LOCATION_CACHE_KEYS.parcels);
    if (cachedParcels) {
      const { data, timestamp } = JSON.parse(cachedParcels);
      data.polygons =
        data.polygons?.filter((p: any) => p.properties?.id !== parcelId && p.id !== parcelId) || [];
      localStorage.setItem(LOCATION_CACHE_KEYS.parcels, JSON.stringify({ data, timestamp }));
    }
    const cachedList = localStorage.getItem(LOCATION_CACHE_KEYS.parcelsList);
    if (cachedList) {
      const { data, timestamp } = JSON.parse(cachedList);
      data.parcels = data.parcels?.filter((p: any) => p.id !== parcelId) || [];
      localStorage.setItem(LOCATION_CACHE_KEYS.parcelsList, JSON.stringify({ data, timestamp }));
    }
  } catch (error) {
    console.error("[Cache] Error updating cache after delete:", error);
    invalidateParcelCache();
  }
};

// ============================================
// CACHE GEOGRAFICA PER POLIGONI (by-coordinates)
// ============================================

interface GeoCache {
  data: any;
  timestamp: number;
  centerLat: number;
  centerLon: number;
  radius: number;
  typeOfArea: number;
  granularity: number;
}

const geoPolygonCache = new Map<string, GeoCache>();
const GEO_CACHE_TTL = 30 * 60 * 1000;
const GEO_BUCKET_SIZE = 0.05;
const getGeoBucket = (lat: number, lon: number): string => {
  const bucketLat = Math.floor(lat / GEO_BUCKET_SIZE) * GEO_BUCKET_SIZE;
  const bucketLon = Math.floor(lon / GEO_BUCKET_SIZE) * GEO_BUCKET_SIZE;
  return `${bucketLat.toFixed(2)},${bucketLon.toFixed(2)}`;
};

const isPointCoveredByCache = (
  targetLat: number,
  targetLon: number,
  targetRadius: number,
  cache: GeoCache
): boolean => {
  const R = 6371000;
  const dLat = ((cache.centerLat - targetLat) * Math.PI) / 180;
  const dLon = ((cache.centerLon - targetLon) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((targetLat * Math.PI) / 180) *
      Math.cos((cache.centerLat * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // distanza in metri
  const isCovered = distance + targetRadius <= cache.radius * 1.1;
  return isCovered;
};

const findCoveringCache = (
  lat: number,
  lon: number,
  radius: number,
  typeOfArea: number,
  granularity: number
): any | null => {
  const now = Date.now();
  const mainBucket = getGeoBucket(lat, lon);
  const bucketsToCheck = [
    mainBucket,
    getGeoBucket(lat + GEO_BUCKET_SIZE, lon),
    getGeoBucket(lat - GEO_BUCKET_SIZE, lon),
    getGeoBucket(lat, lon + GEO_BUCKET_SIZE),
    getGeoBucket(lat, lon - GEO_BUCKET_SIZE),
  ];

  for (const bucket of bucketsToCheck) {
    const cacheKey = `${bucket}_${typeOfArea}_${granularity}`;
    const cached = geoPolygonCache.get(cacheKey);

    if (cached) {
      if (now - cached.timestamp > GEO_CACHE_TTL) {
        geoPolygonCache.delete(cacheKey);
        continue;
      }
      if (isPointCoveredByCache(lat, lon, radius, cached)) {
        return cached.data;
      }
    }
  }
  return null;
};

/**
 * Salva nella cache geografica
 */
const saveToGeoCache = (
  lat: number,
  lon: number,
  radius: number,
  typeOfArea: number,
  granularity: number,
  data: any
) => {
  const bucket = getGeoBucket(lat, lon);
  const cacheKey = `${bucket}_${typeOfArea}_${granularity}`;

  geoPolygonCache.set(cacheKey, {
    data,
    timestamp: Date.now(),
    centerLat: lat,
    centerLon: lon,
    radius,
    typeOfArea,
    granularity,
  });
  if (geoPolygonCache.size > 50) {
    const oldestKey = Array.from(geoPolygonCache.entries()).sort(
      (a, b) => a[1].timestamp - b[1].timestamp
    )[0][0];
    geoPolygonCache.delete(oldestKey);
  }
};

export const invalidateGeoCache = () => {
  geoPolygonCache.clear();
};

export const getOrganizationStatus = async (config: AxiosRequestConfig = {}) => {
  const response = await api<string[]>({
    url: `${prefix}/${namespace}/rolesstatuses`,
    method: "GET",
    ...config,
  });
  return response;
};

export const getOrganizationById = async (id: number, config: AxiosRequestConfig = {}) => {
  const response = await api<OrganizationDetailType>({
    url: `${prefix}/${namespace}/${id}/details`,
    method: "GET",
    ...config,
  });
  return response;
};

export const createOrganization = async (params: object, config: AxiosRequestConfig = {}) => {
  const response = await api<{
    id: number;
  }>({
    url: `${prefix}/${namespace}`,
    method: "POST",
    data: params,
    ...config,
  });
  return response;
};

export const updateOrganization = async (
  id: number | string,
  params: object,
  config: AxiosRequestConfig = {}
) => {
  const response = await api<void>({
    url: `${prefix}/${namespace}/${id}`,
    method: "PUT",
    data: params,
    ...config,
  });
  return response;
};

export const getEntityByOrganizationId = async (
  id: number,
  params: GenericParamsType & { entityType: string },
  config: AxiosRequestConfig = {}
) => {
  const query = generateQueryParamFromObj(params);
  const response = await api<{ data: OrganizationEntityType[]; count: number }>({
    url: `${prefix}/${namespace}/${id}/entities?${query}`,
    method: "get",
    ...config,
  });
  return response;
};

export const getMapMarkers = async (
  params: OrganizationsMapParamsType = {},
  config: AxiosRequestConfig = {}
) => {
  const query = generateQueryParamFromObj(params);
  const response = await api<{ data: SingleOrganizationMap[] }>({
    url: `${prefix}/${namespaceMap}?${query}`,
    method: "get",
    ...config,
  });
  return response;
};

export const getAllOrganizationsWithCache = async (
  params: OrganizationsMapParamsType = {},
  config: AxiosRequestConfig = {}
) => {
  const persistentCacheKey = LOCATION_CACHE_KEYS.allOrganizations;

  return withLocationCache(
    persistentCacheKey,
    async () => {
      try {
        const query = generateQueryParamFromObj(params);
        const response = await api<{ data: any }>({
          url: `${prefix}/${namespaceMap}?${query}`,
          method: "get",
          ...config,
        });

        // Normalizza la struttura dati
        return {
          organizations: response?.data?.organizations ?? [],
          invitations: response?.data?.invitations ?? [],
          organizationsPoints: response?.data?.organizationsPoints ?? [],
          invitationsPoints: response?.data?.invitationsPoints ?? [],
        };
      } catch (error) {
        console.error("Errore caricamento organizzazioni:", error);
        throw error;
      }
    },
    { ttl: Infinity } // Cache infinita - ricarica solo su invalidazione manuale
  );
};

export const getRegions = async (
  params: {
    request?: string;
  } = {},
  config: AxiosRequestConfig = {}
) => {
  const cacheKey = `${LOCATION_CACHE_KEYS.regions}_${params.request || "default"}`;

  return withLocationCache(cacheKey, async () => {
    const query = generateQueryParamFromObj(params);
    const response = await api<{ data: [] }>({
      url: `${prefix}/${mapService}/regions?${query}`,
      method: "get",
      ...config,
    });
    return response;
  });
};

export const getParcels = async (config: AxiosRequestConfig = {}) => {
  const url = `${prefix}/${mapService}/parcels`;
  const persistentCacheKey = LOCATION_CACHE_KEYS.parcelsList;
  return withLocationCache(
    persistentCacheKey,
    async () => {
      try {
        const abortController = new AbortController();
        const response = await api<{ parcels: Array<any> }>({
          url,
          method: "get",
          signal: abortController.signal,
          ...config,
        });

        return response;
      } catch (error: any) {
        if (error.name !== "AbortError") {
          console.error("Errore in getParcels:", error.message);
        }
        throw error;
      }
    },
    { ttl: 7 * 24 * 60 * 60 * 1000 } // Cache 7 giorni
  );
};

export const getProvinces = async (
  params: {
    regionIds?: number[];
  } = {},
  config: AxiosRequestConfig = {}
) => {
  const cacheKey = `${LOCATION_CACHE_KEYS.provinces}${params.regionIds?.join(",") || "all"}`;

  return withLocationCache(cacheKey, async () => {
    const queryString = params.regionIds?.map((id) => `regionId=${id}`).join("&");
    const response = await api<{ data: [] }>({
      url: `${prefix}/${mapService}/provinces${queryString ? `?${queryString}` : ""}`,
      method: "get",
      ...config,
    });
    return response;
  });
};

export const getCities = async (
  params: {
    provinceIds?: number[];
  } = {},
  config: AxiosRequestConfig = {}
) => {
  const cacheKey = `${LOCATION_CACHE_KEYS.cities}${params.provinceIds?.join(",") || "all"}`;

  return withLocationCache(cacheKey, async () => {
    const queryString = params.provinceIds?.map((id) => `provinceId=${id}`).join("&");
    const response = await api<{ data: [] }>({
      url: `${prefix}/${mapService}/cities${queryString ? `?${queryString}` : ""}`,
      method: "get",
      ...config,
    });
    return response;
  });
};

export const getZipCodes = async (
  params: {
    cityIds?: number[];
  } = {},
  config: AxiosRequestConfig = {}
) => {
  const cacheKey = `${LOCATION_CACHE_KEYS.zipCodes}${params.cityIds?.join(",") || "all"}`;

  return withLocationCache(cacheKey, async () => {
    const queryString = params.cityIds?.map((id) => `cityId=${id}`).join("&");
    const response = await api<{ data: [] }>({
      url: `${prefix}/${mapService}/cities/caps${queryString ? `?${queryString}` : ""}`,
      method: "get",
      ...config,
    });
    return response;
  });
};

const lastRequestCache = new Set<number>(); // Solo typeOfArea memorizzati

export const getMapPolygons = async (
  params: {
    latitude?: number;
    longitude?: number;
    radius?: number;
    typeOfArea?: number;
    granularity?: number;
  },
  config: AxiosRequestConfig = {}
): Promise<{ polygons: Array<any> }> => {
  const url = `${prefix}/${mapService}/polygons/by-coordinates`;

  const lat = params.latitude;
  const lon = params.longitude;
  const radius = params.radius || 50000;
  const typeOfArea = params.typeOfArea ?? 4; // Default: CAP/comuni
  const granularity = params.granularity !== undefined ? params.granularity : 1; // ✅ Fix: 0 è un valore valido!
  const isRegion = typeOfArea === 0 && granularity === 0;
  if (isRegion) {
    const persistentCacheKey = `${LOCATION_CACHE_KEYS.regions}${radius}`;

    return withLocationCache(
      persistentCacheKey,
      async () => {
        try {
          const response = await api<{ polygons: Array<any> }>({
            url,
            method: "get",
            params,
            ...config,
          });
          return { polygons: response.data?.polygons || response.polygons || [] };
        } catch (error) {
          console.error("Errore caricamento regioni:", error);
          throw error;
        }
      },
      { ttl: Infinity } // Cache infinita - regioni non cambiano MAI
    );
  }
  const isProvince = typeOfArea === 1 && granularity === 0;
  if (isProvince) {
    const persistentCacheKey = `${LOCATION_CACHE_KEYS.provinces}${radius}`;
    return withLocationCache(
      persistentCacheKey,
      async () => {
        try {
          const response = await api<{ polygons: Array<any> }>({
            url,
            method: "get",
            params,
            ...config,
          });
          return { polygons: response.data?.polygons || response.polygons || [] };
        } catch (error) {
          console.error("Errore caricamento province:", error);
          throw error;
        }
      },
      { ttl: Infinity } // Cache infinita - province non cambiano MAI
    );
  }
  if (lat !== undefined && lon !== undefined) {
    const cachedData = findCoveringCache(lat, lon, radius, typeOfArea, granularity);
    if (cachedData) {
      return cachedData; // Cache hit - ritorna immediatamente
    }
  }

  try {
    cancelPreviousRequests(url); // Annulla richieste precedenti
    const abortController = new AbortController();
    registerNewRequest(url, abortController); // Registra la nuova richiesta
    const response = await api<{ polygons: Array<any> }>({
      url,
      method: "get",
      params,
      signal: abortController.signal,
      ...config,
    });
    if (lat !== undefined && lon !== undefined) {
      saveToGeoCache(lat, lon, radius, typeOfArea, granularity, response);
    }

    return response;
  } catch (error) {
    if (error.name !== "AbortError") {
      console.error("Errore nella richiesta:", error);
    }
    throw error;
  }
};

export const getFilteredMapPolygons = async (
  params: {
    filters?: {
      latitude?: number;
      longitude?: number;
      radius?: number;
      granularity?: number;
      typeOfArea?: AreaType;
    };
    mustExcludeEntities?: boolean;
    entityIds?: number[];
  } = {},
  config: AxiosRequestConfig = {}
) => {
  const response = await api<{ polygons: Array<any> }>({
    url: `${prefix}/${mapService}/polygons-filtered`,
    method: "post",
    data: params,
    ...config,
  });
  return response;
};

export const getMapParcels = async (
  params: {
    latitude?: number;
    longitude?: number;
    radius?: number;
  } = {},
  config: AxiosRequestConfig = {}
) => {
  const url = `${prefix}/${mapService}/parcels/polygon`;
  const persistentCacheKey = LOCATION_CACHE_KEYS.parcels;
  return withLocationCache(
    persistentCacheKey,
    async () => {
      try {
        const abortController = new AbortController();
        const response = await api<{ polygons: Array<any> }>({
          url,
          method: "get",
          params,
          signal: abortController.signal,
          ...config,
        });

        return response;
      } catch (error: any) {
        console.error("Errore in getMapParcels:", error.message);
        throw error;
      }
    },
    { ttl: 7 * 24 * 60 * 60 * 1000 } // Cache 7 giorni
  );
};

export const getRegionsPolygons = async (config: AxiosRequestConfig = {}) => {
  const url = `${prefix}/${mapService}/polygons/regions`;
  const params = config.params || {};
  const persistentCacheKey = LOCATION_CACHE_KEYS.regions;

  return withLocationCache(
    persistentCacheKey,
    async () => {
      try {
        const abortController = new AbortController();
        const response = await api<{ polygons: Array<any> }>({
          url,
          method: "get",
          signal: abortController.signal,
          ...config,
        });

        return response;
      } catch (error) {
        console.error("Errore in getRegionsPolygons:", error.message);
        throw error;
      }
    },
    { ttl: Infinity } // Cache infinita - regioni non cambiano MAI
  );
};

export const getAccordionRegions = async (config: AxiosRequestConfig = {}) => {
  const cacheKey = `${LOCATION_CACHE_KEYS.regions}_aggregates`;

  return withLocationCache(
    cacheKey,
    async () => {
      const response = await api<{ regionParcelCounters: any }>({
        url: `${prefix}/${mapService}/parcels/aggregates/regions`,
        method: "get",
        ...config,
      });
      return response;
    },
    { ttl: 604800000 } // 7 giorni - i contatori cambiano raramente
  );
};

export const getAccordionProvinces = async (regionId: number, config: AxiosRequestConfig = {}) => {
  const response = await api<{ provinceParcelCounters: any }>({
    url: `${prefix}/${mapService}/parcels/aggregates/regions/${regionId}/provinces/`,
    method: "get",
    ...config,
  });
  return response;
};

export const getPolygonByParcelId = async (parcelId: number, config: AxiosRequestConfig = {}) => {
  const response = await api<any>({
    url: `${prefix}/${mapService}/parcels/${parcelId}`,
    method: "get",
    ...config,
  });
  return response;
};

export const createParcelPolygon = async (
  params: {
    name: string;
    description: string;
    polygonIds: Array<number>;
    status: number;
  },
  config: AxiosRequestConfig = {}
) => {
  const response = await api<{ polygons: Array<any> }>({
    url: `${prefix}/${mapService}/parcels`,
    method: "post",
    data: params,
    ...config,
  });
  if (response?.polygon) {
    updateParcelCacheAfterCreate(response.polygon);
  } else {
    // Fallback: invalida cache se non abbiamo i dati della nuova particella
    invalidateParcelCache();
  }

  return response;
};

export const updateParcelPolygonName = async (
  id: number | any,
  params: {
    name: string;
    description: string;
  },
  config: AxiosRequestConfig = {}
) => {
  const response = await api<{ polygons: Array<any> }>({
    url: `${prefix}/${mapService}/parcels/${id}`,
    method: "put",
    data: params,
    ...config,
  });
  updateParcelCacheAfterUpdate(id, { name: params.name, description: params.description });

  return response;
};

export const updateParcelPolygonIds = async (
  id: number | any,
  params: {
    polygonIds: Array<number>;
  },
  config: AxiosRequestConfig = {}
) => {
  const response = await api<{ polygons: Array<any> }>({
    url: `${prefix}/${mapService}/parcels/${id}/polygons`,
    method: "put",
    data: params,
    ...config,
  });
  updateParcelCacheAfterUpdate(id, { polygonIds: params.polygonIds });

  return response;
};

export const deleteParcel = async (id: number | any, config: AxiosRequestConfig = {}) => {
  const response = await api<{ polygons: Array<any> }>({
    url: `${prefix}/${mapService}/parcels/${id}`,
    method: "delete",
    ...config,
  });
  updateParcelCacheAfterDelete(id);

  return response;
};

export const setParcelColorTEMP = async (
  parcelId: number,
  statusID: number,
  config: AxiosRequestConfig = {}
) => {
  try {
    const response = await api<{ polygon: any }>({
      url: `${prefix}/${mapService}/parcels/${parcelId}/status`,
      method: "patch",
      params: {
        status: statusID,
      },
      ...config,
    });
    const polygonStore = usePolygon();
    const statusColor = polygonStore.templateColorArray[statusID]?.color;
    updateParcelCacheAfterUpdate(parcelId, {
      status: statusID,
      color: statusColor, // Aggiorna anche il color basato sul nuovo status
    });

    return response;
  } catch (error) {
    console.error("Errore nel set dello status:", error);
    throw error;
  }
};
export const updateParcelStatus = setParcelColorTEMP;

export const invalidateCacheByPrefix = (prefixToInvalidate: string) => {
  for (const key of requestCache.keys()) {
    if (key.startsWith(prefixToInvalidate)) {
      requestCache.delete(key);
    }
  }
};
